/* eslint-disable prefer-const */
import { formatPhoneNumber, formatEmailAddress, changePhoneNumberFormat, setAction, formatAddress, appendDescription, formatAddressString, trimString } from './shared';
import audaTargetMappings from '../../constants/audaTargetDamageAreaMappings';
import translation from '../../../translations/fr.json'
import { getCookie, cookies } from '../../common/cookies';
import { mapLossCause } from '../../constants/lossScenarioMappings';

let defaultProvince;

export const formatDescriptionAuto = (formData) => {
  // description for whether vehicle parked
  if (formData.wasVehicleParked) {
    appendDescription(formData, 'Vehicle was parked');
  } else if (formData.accidentDescription) {
    appendDescription(formData, formData.accidentDescription);
  }

  // description for collision with other vehicles - other - please tell us what happend
  if (formData.pleaseTellUsWhatHappened) {
    appendDescription(formData, formData.pleaseTellUsWhatHappened);
  }

  // description for 'how did you discover the damage to your vehicle
  if (formData.otherInformation) {
    appendDescription(formData, formData.otherInformation);
  }
  // description for demostic animal
  if (formData.domesticAnimalDescription) {
    appendDescription(formData, formData.domesticAnimalDescription);
  }
  // abonly accident description
  if (formData.abOnlyDescription) {
    appendDescription(formData, formData.abOnlyDescription);
  }
};

export const formatHitObject = (formData) => {
  // format hit object information
  if (formData.typeOfObjectHit) {
    formData.hitObject = {};
    formData.hitObject.type = formData.typeOfObjectHit;
    let hitObjectDescription = mapLossCause(formData.typeOfObjectHit, 'auto');
    if (formData.typeOfObjectHit === 'other' && formData.explainOtherColission) {
      hitObjectDescription = `${hitObjectDescription} - ${formData.explainOtherColission}`;
    }
    appendDescription(formData, hitObjectDescription);
  }
  // format animal owner information
  if (formData.owner?.phoneNumber) {
    formData.owner.phoneNumber = changePhoneNumberFormat(formData.owner.phoneNumber) || '999-999-9999';
  }
};

const formatThirdPartyIncident = (tp, index) => {
  let tpIncident = {
    driver: tp.id ? { id: tp.id } : { temporaryId: tp.temporaryId },
    lossParty: 'third_party',
    ownerType: 'other',
    temporaryId: tp.vehicleIncidentTempId,
    numberOfOccupants: tp.peopleInTpVehicle,
    id: tp.vehicleIncidentId,
  };
  // add tp insurance info and vehicle
  let vehicle;
  let insuranceInfo;
  if (tp.vehicle) { // tell if it is abonly
    vehicle = {
      year: trimString(tp.vehicle.year) || '2000',
      make: trimString(tp.vehicle.make) || 'Other',
      model: trimString(tp.vehicle.model) || 'Unknown',
    };
    insuranceInfo = {
      details: 'unknown',
      policyNumber: trimString(tp.vehicle.policyNumber) || null,
      carrier: {
        companyName: trimString(tp.vehicle.insurer) || '999',
      },
    };
  } else {
    vehicle = {
      year: '2000',
      make: 'other',
      model: `Vehicle${index + 1}`,
      bodyType: 'fourDoor',
    };
    insuranceInfo = {
      policyNumber: trimString(tp.insuranceInfo?.policyNumber) || null,
      carrier: {
        companyName: trimString(tp.insuranceInfo?.insurer) || '999',
      },
      details: 'unknown',
    };
  }
  tpIncident.vehicle = vehicle;
  tpIncident.thirdParty = {};
  tpIncident.thirdParty.tpInsurance = insuranceInfo;
  setAction(tpIncident);
  return tpIncident;
};

export const formatThirdPartyContact = (tp, role) => {
  let tpContact = {
    id: tp.id,
    temporaryId: tp.temporaryId,
    firstName: trimString(tp.firstName) || 'Unknown',
    lastName: trimString(tp.lastName) || 'Unknown',
    email: tp.email,
    phoneNumber: {
      code: 'CA',
      number: changePhoneNumberFormat(tp.number) || '999-999-9999',
    },
    driverLicenseNumber: trimString(tp.insuranceInfo?.licenseNumber) || null,
    licenseState: trimString(tp.insuranceInfo?.issuingProvince) || null,
    type: 'person',
    role: [role],
  };
  tpContact.address = formatAddress(tp, defaultProvince);
  return tpContact;
};

const formatThirdPartyInjury = (tp) => {
  if (tp.applyThirdParty && tp.applyThirdParty !== 'notInjured' && tp.applyThirdParty !== 'iDontKnow') {
    let injuredParty;
    if (tp.id) {
      injuredParty = { id: tp.id };
    } else if (tp.temporaryId) {
      injuredParty = { temporaryId: tp.temporaryId };
    }
    let injuryIncident = {
      injuredParty,
      tpInjuryDetails: tp.applyThirdParty,
    };
    if (tp.injuryIncidentId) {
      injuryIncident.id = tp.injuryIncidentId;
      injuryIncident.action = 'patch';
    } else if (tp.injuryIncidentTemporaryId) {
      injuryIncident.temporaryId = tp.injuryIncidentTemporaryId;
      injuryIncident.action = 'post';
    }
    return injuryIncident;
  }
};

// Format loss location
export const formatLossLocationAuto = (formData) => {
  const lossLocation = formData.lossLocationAddress;
  if (lossLocation) {
    defaultProvince = JSON.parse(sessionStorage.getItem('thePolicy'))?.policyLocation?.[0]?.provinceCode;
    let formattedLossLocation = {
      streetNumber: lossLocation.street !== '' ? trimString(lossLocation.street) : null,
      streetName: trimString(lossLocation.streetName) || 'Unknown',
      city: lossLocation.zipCode ? formatAddressString([lossLocation.city, lossLocation.province || lossLocation.state, lossLocation.zipCode]) : lossLocation.city || null,
      countryCode: lossLocation.country,
      postalCode: trimString(lossLocation.postalCode) || null,
      geoLocation: {
        latitude: lossLocation.coords?.lat,
        longitude: lossLocation.coords?.lng,
      },
      mapURI: lossLocation.url,
    };
    if (!lossLocation.coords) { // delete geoLocation object if it is empty
      delete formattedLossLocation.geoLocation;
    }
    formattedLossLocation.provinceCode = lossLocation.country === 'CA' ? (lossLocation.province || defaultProvince) : null;

    setAction(formattedLossLocation);
    formData.lossLocation = formattedLossLocation;
  }
};

export const formatVehicleIncident = (formData) => {
  let vehicleIncident = [];
  // add the insured vehicle when it is not an abOnly claim and driver is specified already.
  let incident = {
    vehicleParked: formData.wasVehicleParked,
    airbagsDeployed: formData.airbagsDeploy,
    missingComponents: formData.arePartsMissing === 'yes',
    odometerRead: trimString(formData.odometerReading) && parseInt(formData.odometerReading, 10),
    vehicleSafelyDriven: formData.vehicleSafelyDriven,
    vehicleOperable: formData.vehicleStartDrive,
    waterEnteredVehicle: formData.waterEnterVehicle,
    vehicleTowed: formData.vehicleTowed,
    wasVehicleHitAndRun: formData.wasVehicleHitAndRun,
    fireDamage: formData.fireDamage,
    lossParty: 'insured',
    temporaryId: formData.policyHolder.vehicleIncidentTempId,
    id: formData.policyHolder.vehicleIncidentId,
  };

  if (formData.towProvider && Object.keys(formData.towProvider).length !== 0) {
    incident.workOrderSigned = false;
    incident.towYardContacted = false;
    incident.settledTowing = false;
    incident.towingAgency = {};
    if (formData.towProvider.id) {
      incident.towingAgency.id = formData.towProvider.id;
    } else {
      incident.towingAgency.temporaryId = formData.towProvider.temporaryId;
    }
  }

  if (formData.whoDrivingVehicle?.name !== 'Unoccupied' && formData.whoDrivingVehicle?.name !== 'Driver not listed') {
    if (formData.whoDrivingVehicle?.id) {
      incident.driver = { id: formData.whoDrivingVehicle.id };
    } else if (formData.whoDrivingVehicle?.policySystemId) {
      incident.driver = { policySystemId: formData.whoDrivingVehicle.policySystemId };
    }
  } else if (formData.driver && Object.keys(formData.driver).length !== 0) { // added driver manually
    if (formData.driver?.id) {
      incident.driver = { id: formData.driver.id };
    } else if (formData.driver?.temporaryId) {
      incident.driver = { temporaryId: formData.driver.temporaryId };
    }
  }

  let vehicle = null;
  if (formData.vehicleId) { // This is abonly vehicle incident
    vehicle = { id: formData.vehicleId };
    incident.ownerType = 'other';
  } else if (formData.vehiclePolicySystemId) { // This is abonly vehicle incident
    vehicle = { policySystemId: formData.vehiclePolicySystemId };
    incident.ownerType = 'other';
  } else if (formData.whichVehicleWasInvolved?.id) {
    vehicle = { id: formData.whichVehicleWasInvolved?.id };
  } else if (formData.whichVehicleWasInvolved?.policySystemId) {
    vehicle = { policySystemId: formData.whichVehicleWasInvolved?.policySystemId };
  }
  if (vehicle) {
    vehicle.currentLocation = formData.vehicleCurrentLocation;
    vehicle.licensePlate = formData.vehicleLicensePlate || null;
  }
  incident.vehicle = vehicle;

  // Add description for fire damage
  if (formData.fireDamage) {
    let fireDamageDescription;
    if (formData.fireDamage === 'partially') {
      fireDamageDescription = 'Partially burned';
    } else if (formData.fireDamage === 'totally') {
      fireDamageDescription = 'Totally burned';
    }
    incident.description = fireDamageDescription;
  }

  const damagesPanel = formData.vehicleIncident?.vehicleDamagesPanel?.parts;
  const language = getCookie(cookies.CMSPreferredCulture)?.substring(0, 2) || 'en';
  if (damagesPanel) {
    damagesPanel.forEach((damage) => {
      damage.area = damage.id;
      if (!incident.description) {
        incident.description = `${audaTargetMappings[damage.id]} - ${damage.severity}`;
        if(language === 'fr'){
          incident.description = translation[incident.description];
        }
      } else {
        if(language === 'fr'){
          const descFR = `${audaTargetMappings[damage.id]} - ${damage.severity}`;
          incident.description = `${incident.description}, ${translation[descFR]}`;
        }
        else{
          incident.description = `${incident.description}, ${audaTargetMappings[damage.id]} - ${damage.severity}`;
        }
      }
      delete damage.id;
    });
    incident.vehicleDamageDetails = damagesPanel;
  }

  if (formData.secLossScenario !== 'injuredPedestrianOrCyclist') {
    let passengers = []; // format passengers array for insured vehicle incident
    formData.passengers?.addPassengerInfo?.forEach((el) => {
      if (el.selected && el.name !== 'No passenger') {
        let passenger;
        if (el.id) {
          passenger = {
            id: el.id,
          };
        } else if (el.policySystemId) {
          passenger = {
            policySystemId: el.policySystemId,
          };
        } else if (el.temporaryId) {
          passenger = {
            temporaryId: el.temporaryId,
          };
        }
        passengers.push(passenger);
      }
    });
    incident.passengers = passengers;
  }
  setAction(incident);
  vehicleIncident.push(incident);

  // add third party vehicle only if third party is not cyclist or pedestrian
  if (formData.secLossScenario !== 'collisionWithCyclist' && formData.secLossScenario !== 'collisionWithPedestrian') {
    if (formData.collectDriversInformation) {
      formData.thirdParty?.peopleInTheOtherVehiclesInfo?.forEach((tp, index) => {
        let tpIncident = formatThirdPartyIncident(tp, index);
        setAction(tpIncident);
        vehicleIncident.push(tpIncident);
      });
      // delete the applyThirdParty attribute on the claim level because we have them under third party level.
      delete formData.applyThirdParty;
    }
  } else if (formData.secLossScenario === 'collisionWithCyclist') {
    formData.tpCyclistInjury = formData.applyThirdParty;
    delete formData.applyThirdParty;
  } else if (formData.secLossScenario === 'collisionWithPedestrian') {
    formData.tpPedestrianInjury = formData.applyThirdParty;
    delete formData.applyThirdParty;
  }
  formData.vehicleIncident = vehicleIncident;
};

export const formatInjuryIncident = (formData) => {
  let injuredInjuries = [];
  // insured party injury incidents
  formData.injuredInjuries?.whatAreTheInjuriesInfo?.forEach((incident) => {
    let injury = {
      injuryScenario: incident.selectAllInjuriesApply?.selectedValues,
      injuryMitigationScenarios: incident.followingApply?.selectedValues,
      itemsDamaged: incident.wereAnyOfTheFollowingItemsDamaged?.selectedValues,
    };
    // match ids
    if (incident.id) {
      injury.id = incident.id;
    } else if (incident.temporaryId) {
      injury.temporaryId = incident.temporaryId;
    }
    let injuredParty;
    if (incident.injuredId) {
      injuredParty = {
        id: incident.injuredId,
      };
    } else if (incident.injuredPolicySystemId) {
      injuredParty = {
        policySystemId: incident.injuredPolicySystemId,
      };
    } else if (incident.injuredTemporaryId) {
      injuredParty = {
        temporaryId: incident.injuredTemporaryId,
      };
    }
    injury.injuredParty = injuredParty;
    // format bodypart
    let bodyParts = [];
    incident.selectBonesBroken?.selectedValues?.forEach((el) => {
      const bodyPart = {
        bodyPart: el,
        description: 'brokenFractured',
      };
      bodyParts.push(bodyPart);
    });
    injury.bodyParts = bodyParts;

    setAction(injury);
    injuredInjuries.push(injury);
  });

  // third party injury incidents
  formData.thirdParty?.peopleInTheOtherVehiclesInfo?.forEach((tp) => {
    const injuryIncident = formatThirdPartyInjury(tp);
    if (injuryIncident) {
      injuredInjuries.push(injuryIncident);
    }
  });
  formData.injuredInjuries = injuredInjuries;
};

export const formatContactInfoAuto = (formData) => {
  let contact = [];
  // add third party contact info
  let tpRole = 'driver';
  if (formData.secLossScenario === 'collisionWithPedestrian') {
    tpRole = 'pedestrian';
  } else if (formData.secLossScenario === 'collisionWithCyclist') {
    tpRole = 'cyclist';
  }
  formData.thirdParty?.peopleInTheOtherVehiclesInfo?.forEach((tp) => {
    let tpContact = formatThirdPartyContact(tp, tpRole);
    setAction(tpContact);
    contact.push(tpContact);
  });

  const policyHolderExist = formData.policyHolder && Object.keys(formData.policyHolder).length !== 0;
  const reporterExist = formData.newReporter && formData.reporter && Object.keys(formData.reporter).length !== 0;
  const driverExist = formData.driver && Object.keys(formData.driver).length !== 0;
  let policyHolderPolicySystemId;
  let policyHolderInfo;
  // add policy holder contact info
  if (policyHolderExist) {
    policyHolderInfo = formData.policyHolder;
    policyHolderPolicySystemId = policyHolderInfo.policySystemId;
    policyHolderInfo.type = 'person';
    policyHolderInfo.role = ['policyholder', 'insured'];
    formatEmailAddress(policyHolderInfo);
    formatPhoneNumber(policyHolderInfo);

    // get name from browser storage
    const policySessionStorage = JSON.parse(window.sessionStorage.getItem('thePolicy'));
    if (policySessionStorage) {
      policyHolderInfo.firstName = policySessionStorage.insured?.firstName;
      policyHolderInfo.lastName = policySessionStorage.insured?.lastName;
    }
    // policy holder will be the reporter if reporter is not specified.
    if (!reporterExist) {
      policyHolderInfo.role.push('reporter');
    }
    // policy holder will be the driver if their digitalId is the same.
    if ((formData.whoDrivingVehicle?.id && formData.whoDrivingVehicle.id === policyHolderInfo.id) || formData.whoDrivingVehicle?.policySystemId === policyHolderInfo.policySystemId) {
      policyHolderInfo.role.push('driver');
    }

    if (formData.secLossScenario === 'injuredPedestrianOrCyclist') {
      delete policyHolderInfo.vehicleIncidentTempId;
    }

    setAction(policyHolderInfo);
    contact.push(policyHolderInfo);
  }
  // add reporter contact info
  if (reporterExist) {
    formData.reporter.type = 'person';
    formData.reporter.role = ['altcontact'];
    formData.reporter.comments = formData.reporter.relationshipToPolicyHolder;
    formData.reporter.address = {
      streetNumber: 'Unknown',
      streetName: 'Unknown',
      city: 'Unknown',
      countryCode: 'CA',
      provinceCode: defaultProvince,
      postalCode: 'X0X 0X0',
    };
    formData.reporter.firstName = formData.reporter.firstName || 'Unknown';
    formData.reporter.lastName = formData.reporter.lastName || 'Unknown';
    formData.reporter.consent = formData.reporter.iAmNotThePolicyHolder || false;
    delete formData.reporter.relationshipToPolicyHolder;
    delete formData.reporter.iAmNotThePolicyHolder;
    formatPhoneNumber(formData.reporter);
    formatEmailAddress(formData.reporter);
    setAction(formData.reporter);
    contact.push(formData.reporter);
  }
  // add driver contact info
  if (driverExist) {
    formData.driver.type = 'person';
    formData.driver.role = ['driver'];

    formData.driver.address = formatAddress(formData.driver, defaultProvince);
    formData.driver.licenseState = trimString(formData.driver.issuingProvince);
    formData.driver.driverLicenseNumber = trimString(formData.driver.licenseNumber) || null;
    formData.driver.firstName = trimString(formData.driver.firstName) || 'Unknown';
    formData.driver.lastName = trimString(formData.driver.lastName) || 'Unknown';
    delete formData.driver.issuingProvince;
    delete formData.driver.licenseNumber;
    formatPhoneNumber(formData.driver);
    formatEmailAddress(formData.driver);
    setAction(formData.driver);
    contact.push(formData.driver);
  }

  // add passengers contact info
  if (formData.passengers?.addPassengerInfo?.length > 0) {
    formData.passengers.addPassengerInfo.forEach((passenger) => {
      if (passenger.selected && passenger.name !== 'No passenger') {
        // update the role of policyHolder if he is also a passenger.
        if (passenger.policySystemId) {
          // injured pedestian/cyclist info is stored in passengers info for abonly scenarios
          if (formData.secLossScenario === 'injuredPedestrianOrCyclist') {
            if (passenger.policySystemId === policyHolderPolicySystemId) {
              policyHolderInfo.role.push('pedestrian');
            } else {
              passenger.role = ['pedestrian'];
              passenger.type = 'person';
              delete passenger.selected;
              delete passenger.isInjured;
              setAction(passenger);
              contact.push(passenger);
            }
          }
        } else {
          passenger.type = 'person';
          // injured people is stored in passengers info in abonly scenario
          if (formData.secLossScenario === 'injuredPedestrianOrCyclist') {
            passenger.role = ['pedestrian'];
          } else {
            passenger.role = ['passenger'];
          }
          passenger.address = formatAddress(passenger, defaultProvince);
          formatPhoneNumber(passenger);
          delete passenger.number;
          delete passenger.name;
          delete passenger.selected;
          delete passenger.isInjured;
          setAction(passenger);
          contact.push(passenger);
        }
      }
    });
  }

  // add tow company contact info
  if (formData.towProvider && Object.keys(formData.towProvider).length !== 0) {
    let towCompany = {
      type: 'AutoTowingAgcy',
      role: ['TowingAgcy'],
      phoneNumber: {
        code: 'CA',
        number: typeof formData.towProvider?.number === 'string' ? changePhoneNumberFormat(formData.towProvider.number) : '999-999-9999',
      },
      companyName: typeof formData.towProvider?.company === 'string' ? trimString(formData.towProvider.company) || 'Unknown' : 'Unknown',
      temporaryId: formData.towProvider?.temporaryId,
      id: formData.towProvider?.id,
    };
    setAction(towCompany);
    contact.push(towCompany);
  }

  formData.contact = contact;
};
